import React, { useState } from 'react';

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MEAL_NAMES = ['Breakfast', 'Lunch', 'Dinner', 'Snacks'];

const getMacroColor = (pct) => {
  if (pct > 110) return 'text-destructive';
  if (pct > 95) return 'text-warning';
  return 'text-success';
};

const SortableFoodItem = ({ item, onRemove, onQuantityChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item?.id });
  const style = { transform: CSS?.Transform?.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const totalCals = Math.round((item?.food?.calories_per_serving || 0) * item?.quantity);
  const totalProtein = Math.round((item?.food?.protein_g_per_serving || 0) * item?.quantity * 10) / 10;
  const totalCarbs = Math.round((item?.food?.carbs_g_per_serving || 0) * item?.quantity * 10) / 10;
  const totalFats = Math.round((item?.food?.fats_g_per_serving || 0) * item?.quantity * 10) / 10;

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 bg-muted/40 rounded-lg border border-border/50 group">
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground p-1">
        <Icon name="GripVertical" size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{item?.food?.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-primary font-semibold">{totalCals} kcal</span>
          <span className="text-xs text-blue-400">P:{totalProtein}g</span>
          <span className="text-xs text-amber-400">C:{totalCarbs}g</span>
          <span className="text-xs text-orange-400">F:{totalFats}g</span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onQuantityChange(item?.id, Math.max(0.25, item?.quantity - 0.25))}
          className="w-5 h-5 rounded bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground"
        >
          <Icon name="Minus" size={10} />
        </button>
        <span className="text-xs font-medium text-foreground w-8 text-center">{item?.quantity}x</span>
        <button
          onClick={() => onQuantityChange(item?.id, item?.quantity + 0.25)}
          className="w-5 h-5 rounded bg-muted hover:bg-muted/80 flex items-center justify-center text-muted-foreground"
        >
          <Icon name="Plus" size={10} />
        </button>
      </div>
      <button
        onClick={() => onRemove(item?.id)}
        className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Icon name="X" size={12} />
      </button>
    </div>
  );
};

const MealCard = ({ meal, mealItems, targets, onDrop, onRemoveItem, onQuantityChange, onReorder }) => {
  const [isOver, setIsOver] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));

  const mealTotals = mealItems?.reduce((acc, item) => ({
    calories: acc?.calories + (item?.food?.calories_per_serving || 0) * item?.quantity,
    protein: acc?.protein + (item?.food?.protein_g_per_serving || 0) * item?.quantity,
    carbs: acc?.carbs + (item?.food?.carbs_g_per_serving || 0) * item?.quantity,
    fats: acc?.fats + (item?.food?.fats_g_per_serving || 0) * item?.quantity,
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 });

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIdx = mealItems?.findIndex(i => i?.id === active?.id);
      const newIdx = mealItems?.findIndex(i => i?.id === over?.id);
      onReorder(meal, arrayMove(mealItems, oldIdx, newIdx));
    }
  };

  const mealIcons = { Breakfast: 'Sunrise', Lunch: 'Sun', Dinner: 'Moon', Snacks: 'Cookie' };

  return (
    <div
      className={`bg-card border rounded-xl overflow-hidden transition-all ${isOver ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'}`}
      onDragOver={e => { e?.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={e => { e?.preventDefault(); setIsOver(false); onDrop(meal, e); }}
    >
      {/* Meal Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name={mealIcons?.[meal] || 'Utensils'} size={14} className="text-primary" />
          </div>
          <span className="text-sm font-semibold text-foreground">{meal}</span>
          <span className="text-xs text-muted-foreground">({mealItems?.length} items)</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-primary">{Math.round(mealTotals?.calories)} kcal</span>
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-blue-400">P:{Math.round(mealTotals?.protein)}g</span>
            <span className="text-xs text-amber-400">C:{Math.round(mealTotals?.carbs)}g</span>
            <span className="text-xs text-orange-400">F:{Math.round(mealTotals?.fats)}g</span>
          </div>
        </div>
      </div>
      {/* Drop Zone / Items */}
      <div className="p-3 min-h-[80px]">
        {mealItems?.length === 0 ? (
          <div className={`flex items-center justify-center h-16 rounded-lg border-2 border-dashed transition-all ${isOver ? 'border-primary bg-primary/5' : 'border-border'}`}>
            <p className="text-xs text-muted-foreground">
              {isOver ? 'Drop food here' : 'Drag food items here'}
            </p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={mealItems?.map(i => i?.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {mealItems?.map(item => (
                  <SortableFoodItem
                    key={item?.id}
                    item={item}
                    onRemove={onRemoveItem}
                    onQuantityChange={onQuantityChange}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

const DayPlanArea = ({ dayPlan, targets, onDrop, onRemoveItem, onQuantityChange, onReorderMeal, onDuplicateDay, selectedDay, onSelectDay, days }) => {
  const dailyTotals = Object.values(dayPlan?.meals || {})?.flat()?.reduce((acc, item) => ({
    calories: acc?.calories + (item?.food?.calories_per_serving || 0) * item?.quantity,
    protein: acc?.protein + (item?.food?.protein_g_per_serving || 0) * item?.quantity,
    carbs: acc?.carbs + (item?.food?.carbs_g_per_serving || 0) * item?.quantity,
    fats: acc?.fats + (item?.food?.fats_g_per_serving || 0) * item?.quantity,
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 });

  const calPct = targets?.calories > 0 ? (dailyTotals?.calories / targets?.calories) * 100 : 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Day Selector */}
      {days?.length > 1 && (
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {days?.map((day, idx) => (
            <button
              key={idx}
              onClick={() => onSelectDay(idx)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                selectedDay === idx
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Day {idx + 1}
            </button>
          ))}
        </div>
      )}
      {/* Day Summary Bar */}
      <div className="flex items-center justify-between mb-4 p-3 bg-card border border-border rounded-xl">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Total Calories</p>
            <p className={`text-lg font-bold ${calPct > 110 ? 'text-destructive' : calPct > 95 ? 'text-warning' : 'text-foreground'}`}>
              {Math.round(dailyTotals?.calories)}
              <span className="text-xs text-muted-foreground font-normal ml-1">/ {Math.round(targets?.calories)}</span>
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div className="text-center">
              <p className="text-xs text-blue-400 font-semibold">{Math.round(dailyTotals?.protein)}g</p>
              <p className="text-xs text-muted-foreground">Protein</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-amber-400 font-semibold">{Math.round(dailyTotals?.carbs)}g</p>
              <p className="text-xs text-muted-foreground">Carbs</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-orange-400 font-semibold">{Math.round(dailyTotals?.fats)}g</p>
              <p className="text-xs text-muted-foreground">Fats</p>
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={onDuplicateDay}>
          <Icon name="Copy" size={14} className="mr-1" />
          Duplicate Day
        </Button>
      </div>
      {/* Meals Grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {MEAL_NAMES?.map(meal => (
            <MealCard
              key={meal}
              meal={meal}
              mealItems={dayPlan?.meals?.[meal] || []}
              targets={targets}
              onDrop={onDrop}
              onRemoveItem={onRemoveItem}
              onQuantityChange={onQuantityChange}
              onReorder={onReorderMeal}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DayPlanArea;
